import React, { useCallback, useEffect, useState } from "react";
import { Clock, CheckCircle, IndianRupee, Loader, XCircle, Wallet } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { inr, inrCompact, fmtDate } from "@/lib/format";
import { PageHeader } from "@/components/PageHeader";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useApp } from "@/context/AppContext";
import { StatusBadgePill, MarkPaidModal, RejectModal, DetailModal } from "@/components/PaymentWithdrawal";

const STATUSES = ["Pending Approval", "Approved", "Payment Processing", "Paid", "Rejected"];

export default function Payments() {
  const { role, can } = useApp();
  const canManage = can("pay.approve") || can("pay.manual") || can("pay.razorpay");
  const [rows, setRows] = useState([]);
  const [ov, setOv] = useState(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [ptype, setPtype] = useState("all");
  const [location, setLocation] = useState("all");
  const [locs, setLocs] = useState([]);
  const [payRec, setPayRec] = useState(null);
  const [rejectRec, setRejectRec] = useState(null);
  const [detailRec, setDetailRec] = useState(null);
  const [acting, setActing] = useState("");

  const load = useCallback(() => {
    const p = new URLSearchParams();
    if (q) p.set("q", q);
    if (status !== "all") p.set("status", status);
    if (ptype !== "all") p.set("ptype", ptype);
    if (location !== "all") p.set("location", location);
    api.get(`/payments?${p}`).then(({ data }) => setRows(data)).catch(() => {});
    api.get("/dashboard/payment-overview").then(({ data }) => setOv(data)).catch(() => {});
  }, [q, status, ptype, location]);
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);
  useEffect(() => { api.get("/locations").then(({ data }) => setLocs(data)).catch(() => {}); }, []);

  const act = async (pid, action, label) => {
    setActing(pid + action);
    try { await api.post(`/payments/${pid}/${action}`); toast.success(label); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); } finally { setActing(""); }
  };

  const cards = ov ? [
    { label: "Pending Approval", value: ov.pending_approval_count, icon: Clock, accent: "amber", tid: "ov-pending-approval" },
    { label: "Approved", value: ov.approved_count, icon: CheckCircle, accent: "blue", tid: "ov-approved" },
    { label: "Pending Payment", value: ov.pending_payment_count, icon: Loader, accent: "cyan", tid: "ov-pending-payment" },
    { label: "Paid", value: ov.paid_count, icon: CheckCircle, accent: "emerald", tid: "ov-paid", sub: inrCompact(ov.paid_amount_total) },
    { label: "Rejected", value: ov.rejected_count, icon: XCircle, accent: "rose", tid: "ov-rejected" },
    { label: "Total Withdrawal", value: inrCompact(ov.total_withdrawal_amount), icon: Wallet, accent: "primary", tid: "ov-total" },
  ] : [];

  return (
    <div>
      <PageHeader title="Withdrawal Requests" subtitle="Approve, process & track DP payouts" />
      <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        {cards.map((c) => (
          <div key={c.label} className="rounded-xl border border-border bg-card p-4" data-testid={c.tid}>
            <div className="flex items-center justify-between">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{c.label}</p>
              <c.icon className={`h-4 w-4 ${c.accent === "emerald" ? "text-emerald-500" : c.accent === "amber" ? "text-amber-500" : c.accent === "cyan" ? "text-cyan-500" : c.accent === "rose" ? "text-rose-500" : c.accent === "blue" ? "text-blue-500" : "text-primary"}`} />
            </div>
            <p className="mt-1.5 font-head text-2xl font-bold tabular">{c.value}</p>
            {c.sub && <p className="text-xs text-muted-foreground">{c.sub}</p>}
          </div>
        ))}
      </div>

      <div className="mb-4 flex flex-wrap gap-2">
        <Input placeholder="Search name / phone / FE ID / Withdrawal ID / UTR…" value={q} onChange={(e) => setQ(e.target.value)} className="max-w-md" data-testid="payments-search" />
        <Select value={status} onValueChange={setStatus}><SelectTrigger className="w-44" data-testid="payments-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Status</SelectItem>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>
        <Select value={ptype} onValueChange={setPtype}><SelectTrigger className="w-40" data-testid="payments-type-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Types</SelectItem><SelectItem value="UPI">UPI</SelectItem><SelectItem value="Bank Transfer">Bank Transfer</SelectItem></SelectContent></Select>
        <Select value={location} onValueChange={setLocation}><SelectTrigger className="w-40" data-testid="payments-location-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Locations</SelectItem>{locs.map((l) => <SelectItem key={l.name} value={l.name}>{l.name}</SelectItem>)}</SelectContent></Select>
      </div>

      <div className="overflow-hidden rounded-xl border border-border bg-card">
        <Table>
          <TableHeader><TableRow className="hover:bg-transparent">
            <TableHead>Request Date</TableHead><TableHead>Withdrawal ID</TableHead><TableHead>Partner</TableHead>
            <TableHead className="text-right">Amount</TableHead><TableHead>Type</TableHead><TableHead>Details</TableHead>
            <TableHead>Status</TableHead><TableHead>Approved By</TableHead><TableHead className="text-right">Action</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {rows.length === 0 && <TableRow><TableCell colSpan={9} className="text-center text-muted-foreground">No withdrawal requests found</TableCell></TableRow>}
            {rows.map((p) => (
              <TableRow key={p.payment_id} data-testid={`payments-row-${p.payment_id}`}>
                <TableCell className="whitespace-nowrap">{fmtDate(p.created_at)}</TableCell>
                <TableCell className="font-mono text-xs">{p.payment_id}</TableCell>
                <TableCell><span className="font-semibold">{p.dp_name}</span><span className="block font-mono text-xs text-muted-foreground">{p.fe_id} · {p.mobile}</span></TableCell>
                <TableCell className="text-right tabular font-semibold">{inr(p.withdrawal_amount)}</TableCell>
                <TableCell className="text-xs">{p.payment_type || p.payment_method}</TableCell>
                <TableCell className="font-mono text-xs">{p.upi_id || (p.payment_type === "BANK TRANSFER" ? "Bank A/C" : "—")}</TableCell>
                <TableCell>
                  <StatusBadgePill s={p.status} />
                  {p.status === "Rejected" && p.rejection_reason && <p className="mt-0.5 max-w-[140px] truncate text-[10px] text-rose-400" title={p.rejection_reason}>{p.rejection_reason}</p>}
                </TableCell>
                <TableCell className="text-xs text-muted-foreground">{p.approved_by || "—"}</TableCell>
                <TableCell className="text-right">
                  <div className="flex flex-wrap items-center justify-end gap-1">
                    <Button size="sm" variant="ghost" className="h-8 px-2 text-xs" data-testid={`view-${p.payment_id}`} onClick={() => setDetailRec(p)}>View</Button>
                    {canManage && p.status === "Pending Approval" && <>
                      <Button size="sm" variant="ghost" className="h-8 px-2 text-xs text-emerald-500" disabled={acting} data-testid={`approve-${p.payment_id}`} onClick={() => act(p.payment_id, "approve", "Approved")}>Approve</Button>
                      <Button size="sm" variant="ghost" className="h-8 px-2 text-xs text-rose-500" data-testid={`reject-${p.payment_id}`} onClick={() => setRejectRec(p)}>Reject</Button>
                    </>}
                    {canManage && p.status === "Approved" &&
                      <Button size="sm" variant="ghost" className="h-8 px-2 text-xs text-cyan-500" disabled={acting} data-testid={`process-${p.payment_id}`} onClick={() => act(p.payment_id, "process", "Processing")}>Process</Button>}
                    {canManage && (p.status === "Approved" || p.status === "Payment Processing") &&
                      <Button size="sm" variant="ghost" className="h-8 px-2 text-xs text-emerald-500" data-testid={`mark-paid-${p.payment_id}`} onClick={() => setPayRec(p)}>Mark Paid</Button>}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <MarkPaidModal rec={payRec} onClose={() => setPayRec(null)} onSaved={load} />
      <RejectModal rec={rejectRec} onClose={() => setRejectRec(null)} onSaved={load} />
      <DetailModal rec={detailRec} onClose={() => setDetailRec(null)} />
    </div>
  );
}
